import { useCallback } from 'react';
import { getNodesBounds, useReactFlow, useStore } from '@xyflow/react';
import {
  WORKFLOW_INSPECTOR_GUTTER,
  WORKFLOW_INSPECTOR_HEIGHT,
  WORKFLOW_INSPECTOR_WIDTH,
  workflowInspectorPlacement,
} from '../components/workflow-edit-layout';

/** Breathing room before a node counts as "too close to an edge to read". */
const REVEAL_MARGIN = 24;

const PAN_MS = 250;

/**
 * Pans a node clear of the floating inspector before it opens.
 *
 * Without this, "keep the canvas visible while editing" is only nominally true —
 * the node you clicked is as likely as not to end up behind the panel.
 *
 * Only pans when it has to. A node already comfortably in view is left exactly
 * where it is, because re-centring on every open would yank the graph away from
 * wherever the user had deliberately scrolled it.
 */
export function useRevealNodeBesideInspector(): (flowNodeId: string) => void {
  const { getNode, getViewport, setCenter } = useReactFlow();
  const width = useStore((state) => state.width);
  const height = useStore((state) => state.height);

  return useCallback(
    (flowNodeId: string) => {
      const node = getNode(flowNodeId);
      if (node === undefined || width === 0 || height === 0) {
        return;
      }

      // `getNodesBounds` resolves absolute position and measured size for us,
      // rather than trusting `node.position` (parent-relative for child nodes)
      // and a guessed width.
      const rect = getNodesBounds([node]);
      const { x: panX, y: panY, zoom } = getViewport();

      const left = rect.x * zoom + panX;
      const top = rect.y * zoom + panY;
      const right = left + rect.width * zoom;
      const bottom = top + rect.height * zoom;

      // The canvas still visible once the inspector is docked. Which edge it eats
      // depends on where it docked, so the clear region — and the direction we
      // nudge the node — follow from the placement, read from the same width the
      // inspector reads, so the two can never disagree.
      const side = workflowInspectorPlacement(width) === 'side';
      const visibleWidth = side
        ? width - WORKFLOW_INSPECTOR_GUTTER - WORKFLOW_INSPECTOR_WIDTH
        : width;
      const visibleHeight = side
        ? height
        : height - WORKFLOW_INSPECTOR_GUTTER - WORKFLOW_INSPECTOR_HEIGHT;

      const alreadyClear =
        left >= REVEAL_MARGIN &&
        right <= visibleWidth - REVEAL_MARGIN &&
        top >= REVEAL_MARGIN &&
        bottom <= visibleHeight - REVEAL_MARGIN;
      if (alreadyClear) {
        return;
      }

      // `setCenter` puts a flow point at the container's centre. We want the node
      // centred in the *visible* region instead, so bias the point we hand it by
      // half of whatever the panel covers — shifting the node that far clear of it.
      const biasX = (width / 2 - visibleWidth / 2) / zoom;
      const biasY = (height / 2 - visibleHeight / 2) / zoom;

      const prefersReducedMotion =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // `setCenter` resolves when the transition finishes; nothing waits on it.
      void setCenter(
        rect.x + rect.width / 2 + biasX,
        rect.y + rect.height / 2 + biasY,
        {
          zoom,
          duration: prefersReducedMotion ? 0 : PAN_MS,
        }
      );
    },
    [getNode, getViewport, setCenter, width, height]
  );
}
